import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router';
import { BreadcrumbData } from 'use-react-router-breadcrumbs';
import { Text, TextVariant } from '@naf/text';
import { Tag } from '@naf-simba/elements-tag';
import { spacing } from '@naf/theme';
import styled from '@emotion/styled';
import { getLabelVariant } from '@lib/getLabelVariant';
import { mapSimbaVariant } from '../../../../utils/simba';
import { BlockContainer } from '../BlockContainer';
import { useDocumentUrlWithNode } from '../../../../hooks/Documents/useDocumentUrl';
import { CenterBlock, HeroWrapper, TextBox, StyledButton } from './styles';
import BreadCrumb from '../../../breadcrumb/BreadCrumb';
import { BreadCrumbContainer } from './BreadCrumbContainer';
import { HeroPictureImage } from './HeroPictureImage';
import { HeroIllustrationImage } from './HeroIllustrationImage';
import { NafLogo } from '../QnaFeedBlock/NafLogo';
import { HeroWithTitleIngressSmallImageAndCtaType } from '../../../../../../types/heroes/heroWithTitleIngressSmallImageAndCta';
import { HeroType } from '../../../../../../types/frontPageType';
import { useTrackPromotion } from '../../../../hooks/useTrackPromotion';
import { getPromotionEventTrackingData } from '../../../../utils/Tracking/Promotion/getPromotionEventTrackingData';
import { useTrackCTA } from '../../../../utils/Tracking/CtaClick/TrackCtaClick';

interface HeroPropsType extends HeroWithTitleIngressSmallImageAndCtaType {
  isFrontPage?: boolean;
}

interface OldHeroPropsType extends HeroType {
  heroVariant: 'heroWithTitleIngressSmallImageAndCta';
  isFrontPage?: boolean;
}

const HeaderNafLogoWrapper = styled.div`
  display: block;
  margin-bottom: ${spacing.space16};
`;

const LabelWrapper = styled.div`
  display: block;
  margin-top: ${spacing.space16};
`;

export const TitleAndIngessImageCtaHero = ({
  breadCrumbs,
  hero: {
    header,
    ctaInternalLink,
    ctaExternalLink,
    image,
    video,
    isFrontPage,
    ingress,
    illustration,
    includeNafLogo,
    label,
  },
}: {
  hero: HeroPropsType | OldHeroPropsType;
  breadCrumbs?: BreadcrumbData[];
}) => {
  const navigate = useNavigate();
  const trackCTA = useTrackCTA();
  const buttonVariant = mapSimbaVariant('secondary');
  const ctaUrl = useDocumentUrlWithNode(ctaInternalLink?.referenceComponent);
  const internalPromotion = ctaInternalLink ? getPromotionEventTrackingData(ctaInternalLink.text, ctaUrl) : undefined;
  const externalPromotion = ctaExternalLink
    ? getPromotionEventTrackingData(ctaExternalLink.text, ctaExternalLink.link)
    : undefined;
  const shouldTrackInternalPromotion = Boolean(internalPromotion);
  const shouldTrackExternalPromotion = Boolean(externalPromotion);
  const labelVariant = label ? getLabelVariant(label.colorVariant) : undefined;
  const { wrapperRef: internalPromotionRef, trackSelectPromotion: trackInternalPromotion } = useTrackPromotion({
    creative_slot: 'hero-with-title-ingress-image-cta',
    creative_name: `CTA | secondary | ${ctaInternalLink?.text ?? ''}`,
    promotion_id: internalPromotion?.promotion_id,
    promotion_name: internalPromotion?.promotion_name,
  });
  const { wrapperRef: externalPromotionRef, trackSelectPromotion: trackExternalPromotion } = useTrackPromotion({
    creative_slot: 'hero-with-title-ingress-image-cta',
    creative_name: `CTA | secondary | ${ctaExternalLink?.text ?? ''}`,
    promotion_id: externalPromotion?.promotion_id,
    promotion_name: externalPromotion?.promotion_name,
  });

  // Do not simplify this - it caused the webapp to break in production
  const [shouldShowBreadcrumbs, setShouldShowBreadcrumbs] = useState(false);
  useEffect(() => {
    setShouldShowBreadcrumbs(breadCrumbs?.length > 1);
  }, [breadCrumbs]);
  // Do not simplify this - end

  return (
    <>
      {shouldShowBreadcrumbs && (
        <BreadCrumbContainer>
          <BreadCrumb />
        </BreadCrumbContainer>
      )}
      <BlockContainer $isFrontPage={isFrontPage} $hasBreadCrumbs={shouldShowBreadcrumbs}>
        <CenterBlock>
          <HeroWrapper>
            <TextBox>
              {includeNafLogo && (
                <HeaderNafLogoWrapper>
                  <NafLogo width="48px" height="48px" />
                </HeaderNafLogoWrapper>
              )}
              {header && (
                <Text tag="h1" variant={TextVariant.Header1}>
                  {header}
                </Text>
              )}
              {ingress && (
                <Text tag="p" variant={TextVariant.Ingress}>
                  {ingress}
                </Text>
              )}
              {label && labelVariant && (
                <LabelWrapper>
                  <Tag text={label.title} color={labelVariant.color} shade={labelVariant.shade} />
                </LabelWrapper>
              )}
              {ctaInternalLink && (
                <div ref={shouldTrackInternalPromotion ? internalPromotionRef : undefined}>
                  <StyledButton
                    variant={buttonVariant.variant}
                    color={buttonVariant.color}
                    shade={buttonVariant.shade}
                    onClick={() => {
                      trackCTA({ cta_position: 'hero', text: ctaInternalLink.text, url: ctaUrl });
                      if (shouldTrackInternalPromotion) {
                        trackInternalPromotion();
                      }
                      if (ctaUrl) {
                        navigate(ctaUrl);
                      }
                    }}
                  >
                    {ctaInternalLink.text}
                  </StyledButton>
                </div>
              )}
              {ctaExternalLink && (
                <div ref={shouldTrackExternalPromotion ? externalPromotionRef : undefined}>
                  <StyledButton
                    variant={buttonVariant.variant}
                    color={buttonVariant.color}
                    shade={buttonVariant.shade}
                    href={ctaExternalLink.link}
                    target={ctaExternalLink.blank ? '_blank' : '_self'}
                    onClick={() => {
                      trackCTA({ cta_position: 'hero', text: ctaExternalLink.text, url: ctaExternalLink.link });
                      if (shouldTrackExternalPromotion) {
                        trackExternalPromotion();
                      }
                    }}
                  >
                    {ctaExternalLink.text}
                  </StyledButton>
                </div>
              )}
            </TextBox>
            {illustration ? <HeroIllustrationImage image={image} /> : <HeroPictureImage image={image} video={video} />}
          </HeroWrapper>
        </CenterBlock>
      </BlockContainer>
    </>
  );
};
